import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { oauthService } from '../oauth';

export function OAuthCallback() {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();
  const hasHandledCallback = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls (React StrictMode in dev runs effects twice)
    if (hasHandledCallback.current) {
      return;
    }
    hasHandledCallback.current = true;

    async function handleCallback() {
      try {
        const returnTo = await oauthService.handleCallback();
        
        if (returnTo) {
          setStatus('success');
          
          // Check if this window was opened from the catalog
          const wasOpenedFromCatalog = sessionStorage.getItem('opened_from_catalog') === 'true';
          
          // If this is a popup window OR was opened from catalog, notify parent/opener windows
          if (window.opener || wasOpenedFromCatalog) {
            console.log('✅ OAuth success, attempting to notify parent windows');
            
            // Try to notify window.opener (if it exists)
            if (window.opener && !window.opener.closed) {
              try {
                console.log('📤 Sending OAUTH_SUCCESS to window.opener');
                window.opener.postMessage(
                  { type: 'OAUTH_SUCCESS' },
                  window.location.origin
                );
              } catch (err) {
                console.error('Failed to notify window.opener:', err);
              }
            }
            
            // Also try to notify all other windows (for catalog tab)
            try {
              console.log('📤 Broadcasting OAUTH_SUCCESS to other windows via BroadcastChannel');
              const channel = new BroadcastChannel('auth_channel');
              channel.postMessage({ type: 'OAUTH_SUCCESS' });
              channel.close();
            } catch (err) {
              console.error('BroadcastChannel not supported or failed:', err);
            }
            
            // Clean up flag
            sessionStorage.removeItem('opened_from_catalog');
            
            // If this was opened from catalog, navigate to the document
            if (wasOpenedFromCatalog && returnTo !== '/') {
              setTimeout(() => {
                navigate(returnTo);
              }, 500);
            } else if (window.opener) {
              // Close popup if it has an opener
              setTimeout(() => {
                window.close();
              }, 500);
            } else {
              setTimeout(() => {
                navigate(returnTo);
              }, 1000);
            }
          } else {
            // Normal navigation for non-popup flow
            setTimeout(() => {
              navigate(returnTo);
            }, 1000);
          }
        } else {
          setStatus('error');
          setErrorMessage('Authentication failed. Please try again.');
          
          // Notify parent window of error if popup
          if (window.opener) {
            window.opener.postMessage(
              { type: 'OAUTH_ERROR', error: 'Authentication failed' },
              window.location.origin
            );
          }
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setErrorMessage('An error occurred during authentication.');
        
        // Notify parent window of error if popup
        if (window.opener) {
          window.opener.postMessage(
            { type: 'OAUTH_ERROR', error: 'An error occurred during authentication' },
            window.location.origin
          );
        }
      }
    }

    handleCallback();
  }, [navigate]);

  return (
    <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh', padding: '2rem' }}>
      {(status === 'processing' || status === 'success') && (
        <div className="text-center">
          <ProgressSpinner />
        </div>
      )}
      
      {status === 'error' && (
        <div className="text-center" style={{ maxWidth: '500px' }}>
          <i className="pi pi-times-circle text-red-500" style={{ fontSize: '4rem' }}></i>
          <h2 className="mt-3 text-red-700">Authentication Failed</h2>
          <Message severity="error" text={errorMessage} className="w-full my-3" />
          <Button 
            label="Go to Home" 
            icon="pi pi-home"
            onClick={() => navigate('/')}
            className="p-button-primary"
          />
        </div>
      )}
    </div>
  );
}
